import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import {
  ActivateAccountPayload,
  BrandingSettings,
  InvoiceData,
  Language,
  SetNewPasswordPayload,
  UpdateAdminselfPayload,
  UpdateMyLanguagePayload,
  UpdateMyselfPayload,
  User,
  ValidateLoginMfaPayload,
} from '@interfaces';

import { AppState } from '@state';
import * as userAuthActions from './user-auth.actions';
import * as userAuthSelectors from './user-auth.selectors';

@Injectable()
export class UserAuthFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public myself$ = this.store.select(userAuthSelectors.selectMyself);

  public loading$ = this.store.select(userAuthSelectors.selectLoading);
  public errorMessage$ = this.store.select(userAuthSelectors.selectErrorMessage);
  public loginStep$ = this.store.select(userAuthSelectors.selectLoginStep);
  public pendingLoginId$ = this.store.select(userAuthSelectors.selectPendingLoginId);
  public maskedPhoneNumber$ = this.store.select(userAuthSelectors.selectMaskedPhoneNumber);
  public mfaErrorMessage$ = this.store.select(userAuthSelectors.selectMfaErrorMessage);

  public loadingBranding$ = this.store.select(userAuthSelectors.selectLoadingBranding);
  public branding$ = this.store.select(userAuthSelectors.selectBranding);

  public userUpdateSuccess$ = this.actions.pipe(ofType(userAuthActions.updateMyselfSuccess));
  public userLanguageUpdateSuccess$ = this.actions.pipe(ofType(userAuthActions.updateMyLanguageSuccess));
  public activateAccountSuccess$ = this.actions.pipe(ofType(userAuthActions.activateAccountSuccess));
  public activateAccountError$ = this.actions.pipe(ofType(userAuthActions.activateAccountError));

  public userUpdateError$ = this.actions.pipe(ofType(userAuthActions.updateMyselfError));
  public userLanguageUpdateError$ = this.actions.pipe(ofType(userAuthActions.updateMyLanguageError));

  public getPasswordResetLinkSuccess$ = this.actions.pipe(ofType(userAuthActions.getPasswordResetLinkSuccess));
  public setNewPasswordSuccess$ = this.actions.pipe(ofType(userAuthActions.setNewPasswordSuccess));

  public loginSuccess$ = this.actions.pipe(ofType(userAuthActions.loginSuccess));
  public loginMfaRequired$ = this.actions.pipe(ofType(userAuthActions.loginMfaRequired));
  public resendLoginMfaCodeSuccess$ = this.actions.pipe(ofType(userAuthActions.resendLoginMfaCodeSuccess));
  public resendLoginMfaCodeError$ = this.actions.pipe(ofType(userAuthActions.resendLoginMfaCodeError));
  public getMyselfSuccess$ = this.actions.pipe(ofType(userAuthActions.getMyselfSuccess));
  public updateMyInvoiceDataSuccess$ = this.actions.pipe(ofType(userAuthActions.updateMyInvoiceDataSuccess));
  public updateMyInvoiceDataError$ = this.actions.pipe(ofType(userAuthActions.updateMyInvoiceDataError));

  public deleteAccountSuccess$ = this.actions.pipe(ofType(userAuthActions.deleteAccountSuccess));
  public deleteAccountError$ = this.actions.pipe(ofType(userAuthActions.deleteAccountError));

  public logout$ = this.actions.pipe(ofType(userAuthActions.logout));

  public activateAccount(payload: ActivateAccountPayload): void {
    this.store.dispatch(userAuthActions.activateAccount({ payload }));
  }

  public login(email: string, password: string, userLangId: Language['id'], redirect: string | null): void {
    this.store.dispatch(userAuthActions.login({ payload: { email, password, userLangId }, redirect }));
  }

  public validateLoginMfa(payload: ValidateLoginMfaPayload, redirect: string | null): void {
    this.store.dispatch(userAuthActions.validateLoginMfa({ payload, redirect }));
  }

  public resendLoginMfaCode(
    email: string,
    password: string,
    userLangId: Language['id'],
    redirect: string | null
  ): void {
    this.store.dispatch(userAuthActions.resendLoginMfaCode({ payload: { email, password, userLangId }, redirect }));
  }

  public resetLoginFlow(): void {
    this.store.dispatch(userAuthActions.resetLoginFlow());
  }

  public logout(opts?: { wasUnauthorized?: boolean; sessionTimedOutLogout?: boolean }): void {
    this.store.dispatch(userAuthActions.logout(opts));
  }

  public deleteAccount(password: string): void {
    this.store.dispatch(userAuthActions.deleteAccount({ password }));
  }

  public getMyself(redirect: string | null): void {
    this.store.dispatch(userAuthActions.getMyself({ redirect }));
  }

  public getMyself$(redirect: string | null): Observable<User> {
    return this.store.select(userAuthSelectors.selectMyself).pipe(
      tap(myself => {
        if (!myself || !myself.role?.key) {
          this.getMyself(redirect);
        }
      }),
      filter(myself => {
        if (!myself || !myself.role?.key) {
          return false;
        }
        return true;
      })
    );
  }

  public updateMyself(payload: UpdateMyselfPayload): void {
    this.store.dispatch(userAuthActions.updateMyself({ payload }));
  }

  public updateMyLanguage(payload: UpdateMyLanguagePayload): void {
    this.store.dispatch(userAuthActions.updateMyLanguage({ payload }));
  }

  public updateMyInvoiceData(invoiceData: InvoiceData): void {
    this.store.dispatch(userAuthActions.updateMyInvoiceData({ invoiceData }));
  }

  public updateAdminself(userId: string, payload: UpdateAdminselfPayload): void {
    this.store.dispatch(userAuthActions.updateAdminself({ userId, payload }));
  }

  public getPasswordResetLink(email: string): void {
    this.store.dispatch(userAuthActions.getPasswordResetLink({ email }));
  }

  public setNewPassword(payload: SetNewPasswordPayload): void {
    this.store.dispatch(userAuthActions.setNewPassword({ payload }));
  }

  public resetErrorMessage(): void {
    this.store.dispatch(userAuthActions.getPasswordResetLinkError({ errorMessage: null }));
  }

  public getBranding(): void {
    this.store.dispatch(userAuthActions.getBrandingSettings());
  }

  public getBranding$(): Observable<BrandingSettings> {
    return this.store.select(userAuthSelectors.selectBranding).pipe(
      tap(branding => {
        if (branding === null) {
          this.getBranding();
        }
      }),
      filter(branding => {
        if (branding === null) {
          return false;
        }
        return true;
      })
    );
  }
}
